import { useState } from 'react';
import PropTypes from 'prop-types';
import VisibilitySensor from 'react-visibility-sensor';
import { useIntl } from 'gatsby-plugin-react-intl';
import uuid from 'react-uuid';

import Container from '../../../Core/Container';
import RichText from '../../../Core/RichText';

import {
  Wrapper,
  Copy,
  PointsBlock,
  Spinner,
  NumberCell,
  Number,
  Value,
} from './styles';

// * Inserts a comma in the approprate location (i.e 10000 = 10,000)
function numberWithCommas(number) {
  return Intl.NumberFormat('en-CA').format(number);
}

// * Takes an int and splits it into an array of ints
function splitToDigit(n) {
  return `${n}`.split('').map(i => {
    return parseInt(i, 10);
  });
}

const OptimumPointRoller = ({ data }) => {
  if (!data) return null;
  const intl = useIntl();
  // * The delay between each number rolling over
  const delayTiming = 0.5;
  const { heading, description, pointValue, tickerStatement, layoutType } =
    data;

  // * Price value to go along with the points
  const maxPrice = pointValue / 1000;

  // * Split the point offer into an array to roll over each number, and insert a comma where needed
  const digits = splitToDigit(numberWithCommas(pointValue));

  // * Split the price value into an array as well to roll over each of the numbers
  const priceDigits = splitToDigit(maxPrice);

  // * Stores the index location of the comma in the digits array
  let comma;

  // const [played, setPlayed] = useState(false);
  // * State to store if we trigger the animation yet or not. Animate starts when spinning is set to true
  const [spinning, setSpinning] = useState(false);

  // * Func is called when the spinner scrolls into view. Starts increment and sets the played state to prevent rerunning of the animation
  const spin = visible => {
    if (visible && !spinning) {
      setSpinning(true);
      // setPlayed(true);
    }
  };

  return (
    <VisibilitySensor onChange={spin}>
      <Container>
        <Wrapper className={layoutType}>
          <Copy>
            <RichText document={heading} />
            <RichText document={description} />
          </Copy>
          <PointsBlock>
            <span className="sr-only">{`${numberWithCommas(
              pointValue,
            )} ${intl.formatMessage({
              id: 'pointsEquals',
            })} ${
              intl.locale === 'fr' ? `${maxPrice}$` : `$${maxPrice}`
            }.`}</span>
            <Spinner aria-hidden="true" role="presentation">
              {digits.map((digit, index) => {
                // eslint-disable-next-line no-restricted-globals
                if (isNaN(digit)) {
                  // * This condition is looking for the comma. Stores its location and sets it to a number that wont animate
                  comma = index;
                  return (
                    <NumberCell key={`${uuid()}`} className="comma">
                      {intl.locale === 'fr' ? (
                        <Number intl={intl.locale}>&nbsp;</Number>
                      ) : (
                        <Number intl={intl.locale}>,</Number>
                      )}
                    </NumberCell>
                  );
                }
                // * The Offset determines the number that is shown. If offset is -4, the number is 5
                const offset = digit === 0 ? 9 : digit - 1;
                // * The total length of this animation is equal to the length of the digits array -1 (for the comma) / 2
                // * The length of the animation for each digit is their index + 1 if theyre before the comma, after the comma the length is just the index
                // * The delay is the animation length of each number * the timing, and it is subtracted from the total animation length
                const delay =
                  comma < index
                    ? index * delayTiming
                    : (index + 1) * delayTiming;
                return (
                  <NumberCell key={`${uuid()}`}>
                    <Number
                      className={spinning ? 'animate' : ''}
                      offset={offset}
                      delay={delay}
                      length={digits.length - 1}
                    >
                      1 2 3 4 5 6 7 8 9 0
                    </Number>
                  </NumberCell>
                );
              })}
              <div>
                <span className="roller-text">&nbsp;points&nbsp;=&nbsp;</span>
              </div>
              <Value intl={intl.locale}>
                {priceDigits.map((digit, index) => {
                  // * The Offset determines the number that is shown. If offset is -4, the number is 5
                  const offset = digit === 0 ? 9 : digit - 1;
                  const delay = (index + 1) * delayTiming;
                  return (
                    <NumberCell key={`${uuid()}`}>
                      <Number
                        className={spinning ? 'animate' : ''}
                        offset={offset}
                        delay={delay}
                        length={digits.length - 1}
                      >
                        1 2 3 4 5 6 7 8 9 0
                      </Number>
                    </NumberCell>
                  );
                })}
              </Value>
            </Spinner>
            {tickerStatement && <p>{tickerStatement}</p>}
          </PointsBlock>
        </Wrapper>
      </Container>
    </VisibilitySensor>
  );
};

OptimumPointRoller.propTypes = {
  data: PropTypes.shape().isRequired,
};

export default OptimumPointRoller;
